/*
 * Biletul intors de portal dupa autentificarea federata.
 *
 * Se citeste la incarcarea modulului, deci INAINTE ca React sa deseneze ceva,
 * si se sterge din bara de adrese in aceeasi respiratie. Un bilet ramas in
 * adresa ajunge in istoricul browserului, in antetul Referer al primei cereri
 * catre alt host si in logurile proxy-ului - adica o parola care circula prin
 * trei locuri in care nimeni n-o cauta.
 *
 * `takeTicket()` il intoarce o singura data. Contractul spune ca biletul se
 * accepta o singura data la api; in dezvoltare, StrictMode monteaza componentele
 * de doua ori, iar fara consumarea asta al doilea schimb ar esua si ar arata ca
 * un SSO stricat, desi primul reusise.
 */

import { isNative, onDeepLink, closeInAppBrowser } from '../lib/platform';

function readFromUrl(): string | null {
  const fromSearch = new URLSearchParams(window.location.search).get('ticket');
  if (fromSearch) return fromSearch;

  /* Si dupa hash: in functie de cum e construit `start_url` si de ce pastreaza
     IdP-ul, portalul poate intoarce omul pe "#/login?ticket=...". Se accepta
     amandoua formele, ca pagina sa nu depinda de un detaliu de configurare. */
  const hash = window.location.hash;
  const question = hash.indexOf('?');
  if (question === -1) return null;
  return new URLSearchParams(hash.slice(question + 1)).get('ticket');
}

function stripFromUrl(): void {
  const search = new URLSearchParams(window.location.search);
  search.delete('ticket');

  let hash = window.location.hash;
  const question = hash.indexOf('?');
  if (question !== -1) {
    const params = new URLSearchParams(hash.slice(question + 1));
    params.delete('ticket');
    const rest = params.toString();
    hash = hash.slice(0, question) + (rest ? `?${rest}` : '');
  }

  /*
   * Un hash care arata a ruta - "#/login", "#/catalog/15" - se scoate de tot.
   *
   * Aplicatia merge pe BrowserRouter, deci calea adevarata e in pathname, iar
   * un "#/login" ramas dupa ce am luat biletul nu mai inseamna nimic: nu duce
   * nicaieri si nu se potriveste cu nicio ruta, dar ramane in bara de adrese si
   * in tot ce copiaza omul de acolo. Vine din perioada HashRouter si din
   * adresele de intoarcere configurate atunci.
   *
   * Numai cele care incep cu "#/". O ancora obisnuita - "#main", cea a
   * legaturii care sare peste antet - e a paginii si ramane pe loc.
   */
  if (hash.startsWith('#/')) {
    hash = '';
  }

  const url =
    window.location.pathname +
    (search.toString() ? `?${search}` : '') +
    (hash && hash !== '#' ? hash : '');

  /* replaceState, nu pushState: biletul n-are voie sa ramana nici in istoric,
     deci nu se creeaza o intrare noua peste cea care il contine. */
  window.history.replaceState(null, '', url);
}

/*
 * Pe web biletul vine in adresa, deci se citeste la incarcarea modulului.
 *
 * In carcasa nativa nu vine niciodata pe drumul asta: acolo ruta e in hash
 * - aplicatia merge pe HashRouter, fiindca nu exista server care sa serveasca
 * `index.html` pe o cale adanca - iar biletul soseste mai tarziu, printr-o
 * adresa proprie pe care o deschide sistemul de operare. Citirea si curatarea de
 * mai jos ar fi nu doar inutile acolo, ci daunatoare: `stripFromUrl()` sterge
 * orice hash care incepe cu "#/", adica exact ruta curenta.
 */
let pending: string | null = isNative ? null : readFromUrl();

if (!isNative && (pending !== null || window.location.hash.startsWith('#/'))) {
  stripFromUrl();
}

/* ------------------------------------------------- sosire intarziata */

type Listener = (ticket: string) => void;

const listeners = new Set<Listener>();

/**
 * Biletul dintr-o adresa proprie, de forma `optimumdesk-catalog://auth?ticket=`.
 *
 * Se parseaza cu URL, nu cu regex: schema proprie e o adresa valida, iar
 * `searchParams` scoate corect si un bilet care contine caractere codate - un
 * JWT are puncte si poate avea `-` si `_`, dar `rawurlencode` din portal codeaza
 * orice altceva.
 */
function ticketFromDeepLink(raw: string): string | null {
  try {
    return new URL(raw).searchParams.get('ticket');
  } catch {
    return null;
  }
}

if (isNative) {
  onDeepLink((url) => {
    const ticket = ticketFromDeepLink(url);

    if (ticket === null) return;

    /* Browserul in-app si-a facut treaba; daca ramane deschis, omul se intoarce
       in aplicatie si vede pagina IdP-ului peste ea. */
    void closeInAppBrowser();

    if (listeners.size === 0) {
      /* Nimeni nu asculta inca - pornirea aplicatiei nu s-a terminat. Se pastreaza
         si il ia `takeTicket()`, ca pe web. */
      pending = ticket;
      return;
    }

    for (const listener of listeners) {
      listener(ticket);
    }
  });
}

/**
 * Anunta cand soseste un bilet dupa pornire. Intoarce dezabonarea.
 *
 * Pe web nu se declanseaza niciodata: acolo biletul e in adresa inca dinainte ca
 * React sa deseneze, deci a fost deja luat de `takeTicket()`.
 */
export function onTicket(listener: Listener): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export function takeTicket(): string | null {
  const ticket = pending;
  pending = null;
  return ticket;
}
